/**
 * `test_dummies` — an empty inspection map. Not a playable location.
 *
 * Reach it with `?map=test_dummies` (DEV builds only — see `index.ts`'s
 * `resolveWorld`). Zoom goes down to **1 metre** here via `WorldData.camera`,
 * because the whole point is to get close enough to read a garment seam, a
 * weapon grip or a foot plant, which the shipped 17-30m orbit cannot do.
 *
 * ## What is laid out, and why these rows
 *
 * Three labelled rows on bare ground, west to east, each dummy's
 * `displayName` saying exactly what it is meant to demonstrate:
 *
 * - **Row A (z=-6): every `NpcActivity`.** All 7 — `stand`, `sit`, `work`,
 *   `aim`, `walk`, `sleep`, `death`. That is the complete steady-state set
 *   the animator can express; there is no eighth.
 * - **Row B (z=0): every armed hold family.** All 5 `ArmedPoseFamily` values
 *   — pistol, rifle, blunt, knife, axe — each on a real weapon key from
 *   `WEAPON_POSE_FAMILY_BY_KEY`, so the grip offsets under test are the
 *   shipped ones. Plus an `aim`-activity rifle, which resolves to a
 *   different clip and a different grip table (`NPC_AIM_GRIP_OFFSET`) than
 *   the standing hold, and is the only way to see that path.
 * - **Row C (z=+6): worn equipment.** The skinned garment and both vest
 *   tiers.
 *
 * ## Two things about this map that are deliberate, not bugs
 *
 * **1. The sitting dummy has a crate under it.** `sit-idle-legs` drops the
 * hips ~0.1 units and `sit-idle-arms` is posed against that exact fold (the
 * one hard-coupled pair in the whole clip library). On bare ground a seated
 * NPC floats. The crate is scenery in service of the clip, not decoration.
 *
 * **2. Every dressed dummy is a player preset.** Garments are equippable on
 * the BASE characters (`char-1..8`) only — never on NPC bodies — so dressing
 * an `npc-*` here would show a combination the game cannot produce.
 *
 * Until 2026-09-17 this section also documented a rig defect: only char-2 of
 * the eight placed its limbs where `char-4-rigged` (what every garment is
 * authored against) placed its own, and the rest were off by 26-147mm at
 * render scale, so a sleeve visibly floated beside the arm. The re-rig closed
 * that to 0.1mm on all eight.
 *
 * That is why Row C no longer carries a MISFIT CONTROL. It used to dress one
 * body known not to fit and label it as such, so that a viewer comparing this
 * map against a screenshot of a badly-fitting character could not mistake a
 * known defect for a broken garment system. With every preset now fitting,
 * the control has nothing left to demonstrate, and what stood in its place is
 * now `dummy-worn-char1` — `char-1` wearing the same jacket as the rest.
 */
import type { WorldData } from '../../types';

/** Bare ground; nothing to walk into. Big enough to orbit every row. */
const GROUND = { width: 48, depth: 66 };

const ROW_A_Z = -9;
const ROW_B_Z = -3;
const ROW_C_Z = 3;
const ROW_D_Z = 9;
const ROW_E_Z = 15;
const ROW_F_Z = 21;
/** West-to-east spacing. 2.6m keeps neighbours out of frame at 1m zoom. */
const STEP = 2.6;

/** Evenly spaced x for slot `i` of `n`, centred on the origin. */
const slotX = (i: number, n: number): number => (i - (n - 1) / 2) * STEP;

/** Every dummy is a bodiless, non-interactable `npc` object — no `npcId`, so
 *  `validateWorld` needs no dialog tree (it requires one per `npcs` entry, and
 *  this map has none). `displayName` carries the label instead. */
function dummy(
  id: string,
  displayName: string,
  modelUrl: string,
  x: number,
  z: number,
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    id,
    kind: 'npc',
    label: displayName,
    displayName,
    position: { x, z },
    size: { w: 0.6, d: 0.6, h: 1.8 },
    color: 0x8a8f7a,
    interactable: false,
    modelUrl,
    modelHeight: 1.8,
    // Face south, toward the camera's default approach.
    bodyFacing: Math.PI,
    ...extra,
  };
}

// ── Row A — all 7 NpcActivity values ────────────────────────────────────────
const ACTIVITIES = [
  ['stand', 'char-1.glb', 'A1 stand'],
  ['sit', 'char-2.glb', 'A2 sit'],
  ['work', 'npc-farmer.glb', 'A3 work'],
  ['aim', 'char-3.glb', 'A4 aim'],
  ['walk', 'char-6.glb', 'A5 walk'],
  ['sleep', 'npc-civ-1.glb', 'A6 sleep'],
  ['death', 'npc-bandit-1.glb', 'A7 death'],
] as const;

const rowA = ACTIVITIES.map(([activity, model, name], i) =>
  dummy(`dummy-act-${activity}`, name, `/models/${model}`, slotX(i, ACTIVITIES.length), ROW_A_Z, {
    npcActivity: activity,
    ...(activity === 'work' ? { heldItemKey: 'rake' } : {}),
    ...(activity === 'aim' ? { heldItemKey: 'carbine' } : {}),
  }),
);

// ── Row B — all 5 armed hold families ───────────────────────────────────────
// Keys are real `WEAPON_POSE_FAMILY_BY_KEY` entries, so these exercise the
// shipped `WEAPON_GRIP_OFFSET` numbers rather than a test-only stand-in.
const HOLDS = [
  ['pistol', 'pistol', 'char-2.glb', 'B1 pistol'],
  ['rifle_bolt', 'rifle', 'char-3.glb', 'B2 rifle'],
  ['hockey_stick', 'blunt', 'char-8.glb', 'B3 blunt'],
  ['hunting_knife', 'knife', 'npc-survivor-6.glb', 'B4 knife'],
  ['axe', 'axe', 'npc-survivor-2.glb', 'B5 axe'],
] as const;

const rowB = HOLDS.map(([itemKey, _family, model, name], i) =>
  dummy(`dummy-hold-${itemKey}`, name, `/models/${model}`, slotX(i, HOLDS.length), ROW_B_Z, {
    npcActivity: 'stand',
    heldItemKey: itemKey,
  }),
);

// ── Row C — worn equipment ──────────────────────────────────────────────────
// `equippedKeys` is read by `model-attach.ts` and handed to the NPC animator,
// which routes garment keys through the same skinned-rebind path the player
// uses and vest keys through the same rigid chest attach. Bodies 1-4 are the
// four that actually fit (see the header); body 5 is the labelled control.
// Ordered so a JACKET sits dead centre (x=0): the spawn is at x=0, so walking
// straight forward lands the camera head-on at the skinned garment — the new
// code path, and the one worth looking at. The probe scene `dummies-worn`
// depends on this ordering.
const WORN = [
  ['dummy-worn-vest-light', 'C1 vest light', 'char-8.glb', ['vest_light']],
  ['dummy-worn-vest-heavy', 'C2 vest heavy', 'char-3.glb', ['plate_vest']],
  ['dummy-worn-jacket-a', 'C3 jacket', 'char-2.glb', ['torso_jacket']],
  ['dummy-worn-full', 'C4 full kit', 'char-2.glb', ['torso_jacket', 'legs_cargo', 'feet_boots']],
  // Was the MISFIT CONTROL (the same jacket on a body known not to fit). The
  // 2026-09-17 re-rig removed the defect it existed to make attributable, so
  // it is now just another preset wearing the kit.
  ['dummy-worn-char1', 'C5 jacket (char-1)', 'char-1.glb', ['torso_jacket']],
] as const;

const rowC = WORN.map(([id, name, model, keys], i) =>
  dummy(id, name, `/models/${model}`, slotX(i, WORN.length), ROW_C_Z, {
    npcActivity: 'stand',
    equippedKeys: [...keys],
  }),
);

// ── Row D — the two one-shot clips ──────────────────────────────────────────
// `playAttack` and `playHitReact` are ONE-SHOTS, so unlike the seven
// `NpcActivity` values they cannot be expressed by static map data at all —
// something has to call them. `index.ts` runs a DEV-only ticker for exactly
// these two ids (see `ONE_SHOT_DEMO`), staggered so they do not fire in
// unison. This is the map's own scaffolding, not engine behaviour: nothing in
// normal play calls either method.
export const ONE_SHOT_DEMO: readonly { objectId: string; kind: 'attack' | 'hitReact'; everySec: number }[] = [
  { objectId: 'dummy-oneshot-attack', kind: 'attack', everySec: 2.4 },
  { objectId: 'dummy-oneshot-hit', kind: 'hitReact', everySec: 1.9 },
];

const rowD = [
  dummy('dummy-oneshot-attack', 'D1 attack', '/models/char-8.glb',
    slotX(0, 2), ROW_D_Z, { npcActivity: 'stand', heldItemKey: 'axe' }),
  dummy('dummy-oneshot-hit', 'D2 hit-react', '/models/char-3.glb',
    slotX(1, 2), ROW_D_Z, { npcActivity: 'stand' }),
];

// ── Rows E and F — the rest of the skinned garment set ──────────────────────
// Every remaining TORSO / LEGS / GLOVES garment, one per dummy, so each can be
// walked up to and inspected on its own at the 1m zoom floor. Bodies are drawn
// only from the four that actually FIT (`char-2`, `char-3`, `char-8`,
// `npc-survivor-6`) — see the header. Putting these on the other bodies would
// show the rig defect seventeen more times and drown the signal; C5 is the one
// place that defect is on show, deliberately and labelled.
// BASE characters only. Garments are equippable on the player presets
// (`char-1..8`) and nowhere else — NPC bodies never wear one, so dressing an
// `npc-*` here would demo a combination the game cannot produce.
//
// ALL EIGHT now, since the 2026-09-17 re-rig: every preset places its limbs
// within 0.1mm of `char-4-rigged`'s bone-to-mesh relationship, so a garment
// authored against the reference fits all of them. Before that re-rig only
// char-2 did, and this list was three bodies long with a deliberate misfit
// control beside it.
const FIT_BODIES = [
  'char-1.glb', 'char-2.glb', 'char-3.glb', 'char-4.glb',
  'char-5.glb', 'char-6.glb', 'char-7.glb', 'char-8.glb',
] as const;

const GARMENT_SHOWCASE = [
  ['torso_leather', 'E1 leather'],
  ['torso_lumbershirt', 'E2 flannel'],
  ['torso_puffer', 'E3 puffer'],
  ['torso_raincoat', 'E4 raincoat'],
  ['torso_tactical-hoodie', 'E5 hoodie'],
  ['torso_trench', 'E6 trench'],
  ['legs_jeans', 'F1 jeans'],
  ['legs_tactical', 'F2 tac trousers'],
  ['legs_cargo', 'F3 cargo'],
  ['legs_shorts-cargo', 'F4 cargo shorts'],
  ['legs_sport-short', 'F5 running shorts'],
  ['glove_mitten', 'F6 mittens'],
  ['feet_boots', 'F7 boots (no FEET slot yet)'],
] as const;

const ROW_E = GARMENT_SHOWCASE.filter(([, n]) => n.startsWith('E'));
const ROW_F = GARMENT_SHOWCASE.filter(([, n]) => n.startsWith('F'));

const showcase = (list: readonly (readonly [string, string])[], z: number) =>
  list.map(([key, name], i) =>
    dummy(`dummy-show-${key}`, name, `/models/${FIT_BODIES[i % FIT_BODIES.length]}`,
      slotX(i, list.length), z, { npcActivity: 'stand', equippedKeys: [key] }));

const rowE = showcase(ROW_E, ROW_E_Z);
const rowF = showcase(ROW_F, ROW_F_Z);

/** The crate the sitting dummy needs (see the header). Sized and placed so
 *  `sit-idle-legs`' dropped hips land on its top face rather than in mid-air. */
const sitProp = {
  id: 'prop-sit-crate',
  kind: 'prop' as const,
  label: 'Crate (seat for A2)',
  position: { x: slotX(1, ACTIVITIES.length), z: ROW_A_Z },
  size: { w: 0.8, d: 0.8, h: 0.45 },
  color: 0x6b5a3e,
  interactable: false,
};

export const map: WorldData = {
  cityName: 'Test Dummies',
  ground: GROUND,
  // Close-inspection camera. `minDistance: 1` is the ask; `zoomSpeed` is
  // dropped from the shipped 1.4 m/notch because at this range two notches
  // would otherwise cross the entire usable band and the wheel would be
  // unusable. `pitch` is flattened from 55 deg so that at 1m the camera looks
  // AT the character rather than down onto the top of its head.
  camera: {
    minDistance: 1,
    maxDistance: 14,
    defaultDistance: 5,
    zoomSpeed: 0.35,
    pitch: (22 * Math.PI) / 180,
  },
  // South of every row. The first version spawned at z=12, which after the
  // row spacing put the player INSIDE row D — three dummies filling the
  // screen before the camera had moved.
  // Flat, bright, neutral, no fog. The shipped dusk-firelight look is a mood
  // for a settlement and is wrong here: the first screenshot of this map came
  // back almost black, with the far rows eaten by fog starting at 28m.
  lighting: {
    background: 0x5a5f63,
    fill: 2.2,
    key: 1.6,
    campfire: 0,
    fog: false,
    ground: 0x6e7169,
    fillGround: 0x6b6f72,
  },
  hidePlayerModel: true,
  spawnPoint: { x: 0, z: 28 },
  objects: [...rowA, ...rowB, ...rowC, ...rowD, ...rowE, ...rowF, sitProp] as WorldData['objects'],
  npcs: {},
  dialog: {},
};
